<template>
  <!-- Modal Overlay -->
  <div
    v-if="isVisible"
    class="fixed inset-0 z-50 overflow-y-auto"
    aria-labelledby="modal-title"
    role="dialog"
    aria-modal="true"
  >
    <div
      class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
    >
      <!-- Background overlay -->
      <div
        class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
        aria-hidden="true"
        @click="closeModal"
      ></div>

      <!-- This element is to trick the browser into centering the modal contents. -->
      <span
        class="hidden sm:inline-block sm:align-middle sm:h-screen"
        aria-hidden="true"
        >&#8203;</span
      >

      <!-- Modal panel -->
      <div
        class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6"
      >
        <!-- Loading State -->
        <LoadingState v-if="loading">Submitting feedback...</LoadingState>

        <!-- Main Content -->
        <div v-else-if="!feedbackSubmitted">
          <!-- Success Icon -->
          <div
            class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4"
          >
            <svg
              class="h-6 w-6 text-green-600"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>

          <!-- Header -->
          <div class="text-center mb-6">
            <h3
              class="text-lg leading-6 font-medium text-gray-900 mb-2"
              id="modal-title"
            >
              Great lesson with {{ teacherName }}!
            </h3>
            <p class="text-sm text-gray-500">
              How was your {{ instrument }} lesson? Your feedback helps us
              improve.
            </p>
          </div>

          <!-- Rating Section -->
          <div class="mb-6">
            <label class="block text-sm font-medium text-gray-700 mb-3">
              Rate your lesson experience
            </label>
            <div class="flex justify-center space-x-2">
              <button
                v-for="star in 5"
                :key="star"
                @click="setRating(star)"
                class="focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded"
                :aria-label="`Rate ${star} star${star !== 1 ? 's' : ''}`"
              >
                <svg
                  :class="[
                    'h-8 w-8 transition-colors duration-150',
                    star <= rating
                      ? 'text-yellow-400'
                      : 'text-gray-300 hover:text-yellow-200',
                  ]"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
                  />
                </svg>
              </button>
            </div>
            <p class="text-center text-sm text-gray-500 mt-2">
              {{ getRatingText(rating) }}
            </p>
          </div>

          <!-- Feedback Text Area -->
          <div class="mb-6">
            <label
              for="feedback"
              class="block text-sm font-medium text-gray-700 mb-2"
            >
              Share your thoughts (optional)
            </label>
            <textarea
              id="feedback"
              v-model="feedback"
              rows="3"
              class="shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
              placeholder="What did you learn? How was the teaching style? Any suggestions?"
            ></textarea>
          </div>

          <!-- Continue Learning Section -->
          <div class="bg-blue-50 rounded-lg p-4 mb-6">
            <h4 class="text-sm font-semibold text-blue-900 mb-2">
              Keep the momentum going!
            </h4>
            <p class="text-sm text-blue-800 mb-3">
              Regular practice and lessons are key to improvement. Book your
              next lesson to maintain progress.
            </p>

            <!-- Package Options -->
            <div v-if="packageDeals.length > 0" class="space-y-2 mb-3">
              <p class="text-xs font-medium text-blue-900">
                Special offers available:
              </p>
              <div
                v-for="deal in packageDeals"
                :key="deal.id"
                class="flex items-center justify-between bg-white rounded p-2 text-xs"
              >
                <span class="text-gray-700">{{ deal.name }}</span>
                <span class="font-semibold text-blue-600"
                  >{{ deal.discount }}% off</span
                >
              </div>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="space-y-3">
            <button
              @click="submitFeedback"
              :disabled="rating === 0"
              class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {{
                rating > 0 ? "Submit Feedback" : "Please rate your lesson first"
              }}
            </button>

            <div class="grid grid-cols-1 gap-2">
              <button
                v-if="false"
                @click="createAccount"
                class="w-full flex justify-center py-2 px-4 border border-primary text-primary bg-white hover:bg-blue-50 rounded-md shadow-sm text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                Create Account
              </button>

              <button
                @click="bookAnotherLesson"
                class="w-full flex justify-center py-2 px-4 border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 rounded-md shadow-sm text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                Book Another Lesson
              </button>
            </div>

            <button
              @click="closeModal"
              class="w-full text-center text-sm text-gray-500 hover:text-gray-700 underline"
            >
              Maybe later
            </button>
          </div>
        </div>

        <!-- Thank You State -->
        <div v-else class="text-center">
          <div
            class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4"
          >
            <svg
              class="h-6 w-6 text-green-600"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>

          <h3 class="text-lg font-medium text-gray-900 mb-2">
            Thank you for your feedback!
          </h3>
          <p class="text-sm text-gray-500 mb-6">
            Your input helps us improve the learning experience for everyone.
          </p>

          <!-- Social Sharing -->
          <div class="bg-blue-50 rounded-lg p-4 mb-6">
            <h4 class="text-sm font-semibold text-blue-900 mb-2">
              Share your musical journey
            </h4>
            <p class="text-sm text-blue-800 mb-3">
              Inspire others to start their musical journey too!
            </p>
            <div class="flex justify-center space-x-3">
              <button
                @click="shareOnSocial('twitter')"
                class="text-blue-400 hover:text-blue-600"
                aria-label="Share on Twitter"
              >
                <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path
                    d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"
                  />
                </svg>
              </button>
              <button
                @click="shareOnSocial('facebook')"
                class="text-blue-600 hover:text-blue-800"
                aria-label="Share on Facebook"
              >
                <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path
                    d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
                  />
                </svg>
              </button>
            </div>
          </div>

          <div class="space-y-3">
            <div class="grid grid-cols-1 gap-2">
              <button
                v-if="false"
                @click="createAccount"
                class="w-full flex justify-center py-2 px-4 border border-primary text-primary bg-white hover:bg-blue-50 rounded-md shadow-sm text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                Create Account
              </button>

              <button
                @click="bookAnotherLesson"
                class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                Book Another Lesson
              </button>
            </div>

            <button
              @click="closeModal"
              class="w-full text-center text-sm text-gray-500 hover:text-gray-700 underline"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from "vue";
import { useRouter } from "vue-router";
import LoadingState from "./LoadingState.vue";

export default {
  name: "PostLessonPrompt",
  components: {
    LoadingState,
  },
  props: {
    lessonId: {
      type: String,
      required: true,
    },
    teacherId: {
      type: String,
      required: true,
    },
    teacherName: {
      type: String,
      default: "Your Teacher",
    },
    instrument: {
      type: String,
      default: "Music",
    },
    isVisible: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["close", "feedback-submitted", "account-creation-requested"],
  setup(props, { emit }) {
    const router = useRouter();

    // State
    const rating = ref(0);
    const feedback = ref("");
    const loading = ref(false);
    const feedbackSubmitted = ref(false);

    // Mock package deals - in a real app, these would come from an API
    const packageDeals = ref([
      { id: 1, name: "4-Lesson Package", discount: 15 },
      { id: 2, name: "8-Lesson Package", discount: 25 },
    ]);

    // All users are now authenticated, no guest mode

    // Methods
    const setRating = (newRating) => {
      rating.value = newRating;
    };

    const getRatingText = (ratingValue) => {
      const ratingTexts = {
        0: "Please select a rating",
        1: "Poor",
        2: "Fair",
        3: "Good",
        4: "Very Good",
        5: "Excellent",
      };
      return ratingTexts[ratingValue] || "";
    };

    const submitFeedback = async () => {
      if (rating.value === 0) return;

      try {
        loading.value = true;

        // In a real app, this would make an API call to save feedback
        const feedbackData = {
          lessonId: props.lessonId,
          teacherId: props.teacherId,
          rating: rating.value,
          feedback: feedback.value,
          submittedAt: new Date().toISOString(),
          isGuestFeedback: false,
        };

        // Simulate API call
        await new Promise((resolve) => setTimeout(resolve, 1000));

        console.log("Feedback submitted:", feedbackData);

        feedbackSubmitted.value = true;
        emit("feedback-submitted", feedbackData);
      } catch (error) {
        console.error("Error submitting feedback:", error);
        // In a real app, show error message to user
      } finally {
        loading.value = false;
      }
    };

    const createAccount = () => {
      const queryParams = {
        redirect: "/dashboard",
        from: "post_lesson",
      };

      emit("account-creation-requested");
      router.push({
        path: "/register",
        query: queryParams,
      });
    };

    const bookAnotherLesson = () => {
      // Navigate to teacher profile or search based on current teacher
      if (props.teacherId) {
        router.push(`/teachers/${props.teacherId}`);
      } else {
        router.push("/teachers");
      }
    };

    const shareOnSocial = (platform) => {
      const shareText = `Just had an amazing ${props.instrument} lesson on Crescendo! 🎵 #MusicLearning #Crescendo`;
      const url = window.location.origin;

      let shareUrl = "";

      if (platform === "twitter") {
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
          shareText
        )}&url=${encodeURIComponent(url)}`;
      } else if (platform === "facebook") {
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
          url
        )}&quote=${encodeURIComponent(shareText)}`;
      }

      if (shareUrl) {
        window.open(shareUrl, "_blank", "width=600,height=400");
      }
    };

    const closeModal = () => {
      emit("close");
    };

    return {
      rating,
      feedback,
      loading,
      feedbackSubmitted,
      packageDeals,
      setRating,
      getRatingText,
      submitFeedback,
      createAccount,
      bookAnotherLesson,
      shareOnSocial,
      closeModal,
    };
  },
};
</script>

<style scoped>
.bg-primary {
  background-color: #3b82f6;
}

.bg-primary-dark {
  background-color: #2563eb;
}

.text-primary {
  color: #3b82f6;
}

.border-primary {
  border-color: #3b82f6;
}

.focus\:ring-primary:focus {
  --tw-ring-color: #3b82f6;
}

.focus\:border-primary:focus {
  --tw-border-opacity: 1;
  border-color: #3b82f6;
}
</style>
